import React from "react";

/**
 * A reusable collapsible card with smooth animation, consistent styling, and localStorage persistence.
 * 
 * Props:
 * - title: string → header text
 * - children: JSX → inner content
 * - storageKey?: string → optional key for localStorage (so open state persists)
 * - defaultOpen?: boolean → whether to start expanded
 * - icon?: ReactNode → optional icon to display before title
 */
export default function CollapsibleCard({
  title,
  children,
  storageKey,
  defaultOpen = false,
  icon = null,
}) {
  const [open, setOpen] = React.useState(() => {
    if (storageKey && typeof window !== "undefined") {
      const saved = localStorage.getItem(storageKey);
      return saved === "1" ? true : saved === "0" ? false : defaultOpen;
    }
    return defaultOpen;
  });

  React.useEffect(() => {
    if (storageKey) {
      try {
        localStorage.setItem(storageKey, open ? "1" : "0");
      } catch {}
    }
  }, [open, storageKey]);

  return (
    <div className="mt-6 w-full">
      {/* Header Button */}
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
        className="w-full relative bg-blue-50 dark:bg-blue-950 hover:bg-blue-100 dark:hover:bg-blue-900
                   border border-blue-200 dark:border-blue-700 rounded-lg px-4 py-3
                   font-semibold text-blue-800 dark:text-blue-200 flex items-center justify-between
                   transition-all duration-300 shadow-sm hover:shadow-md"
      >
        <div className="flex items-center gap-2">
          {icon}
          <span>{title}</span>
        </div>
        <span
          className={`ml-2 text-lg transform transition-transform duration-300 ease-out
                      ${open ? "rotate-180 opacity-100" : "rotate-0 opacity-80"}`}
        >
          ▼
        </span>
      </button>

      {/* Collapsible Body */}
      <div
        className={`transition-all duration-300 overflow-hidden
                    ${open ? "max-h-[2000px] opacity-100 mt-3" : "max-h-0 opacity-0"}`}
      >
        <div className="p-6 bg-white/70 dark:bg-gray-800/70 border border-gray-200/80 dark:border-gray-700/80 rounded-xl shadow-inner">
          {children}
        </div>
      </div>
    </div>
  );
}
